/* @flow */

import React, { useContext } from "react";

import { StoreInfoContext } from "entrypoint/shared";
import { useLocation } from "react-router-dom";
import { useTranslate } from "@awardit/react-use-translate";
import { Helmet } from "react-helmet-async";
import Wrapper from "components/Wrapper";
import { formatMeta } from "helpers/get-meta";
import { ButtonLink } from "@crossroads/ui-components";
import PopularBrands from "components/CompanyView/PopularBrands";

import cn from "classnames";
import styles from "./styles.scss";

const InfoCard = ({ children, text }: { children: React$Node, text: string }): React$Node => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.iconContainer}>{children}</div>
      <h3 className={styles.subHeading}>{text}</h3>
    </div>
  );
};

const Hero = (): React$Node => {
  const t = useTranslate();
  return (
    <div className={cn(styles.split, styles.hero)}>
      <div className={styles.textSplit}>
        <h1 className={cn(styles.heading, styles.heroHeading)}>{t("COMPANYVIEW.HERO.TITLE")}</h1>
        <p className={styles.heroText}>{t("COMPANYVIEW.HERO.CONTENT")}</p>
        <div className={styles.list}>
          <ul>
            <li>{t("COMPANYVIEW.HERO.LIST_ITEM_1")}</li>
            <li>{t("COMPANYVIEW.HERO.LIST_ITEM_2")}</li>
            <li>{t("COMPANYVIEW.HERO.LIST_ITEM_3")}</li>
          </ul>
        </div>
        <div className={styles.ctaBox}>
          <ButtonLink
            variant="primary"
            className={styles.cta}
            to={t("COMPANYVIEW.CTA_LINK_1")}
            target="_blank"
          >
            {t("COMPANYVIEW.CTA_1")}
          </ButtonLink>
          <ButtonLink
            variant="ghost"
            to={t("COMPANYVIEW.CTA_LINK_2")}
            className={styles.cta}
            target="_blank"
          >
            {t("COMPANYVIEW.CTA_2")}
          </ButtonLink>
        </div>
      </div>
      <div className={styles.imageWrapper}>
        <img className={styles.imageSplit} src="/assets/company/summer_zg.jpg" alt="" />
      </div>
    </div>
  );
};

const CompanyView = (): React$Node => {
  const t = useTranslate();
  const { info } = useContext(StoreInfoContext);
  const location = useLocation();
  const meta = formatMeta({
    title: t("COMPANYVIEW.HERO.TITLE"),
    description: t("COMPANYVIEW.FIELD_1.TEXT_CONTENT"),
    canonicals: [{ rel: "canonical", href: info.baseUrl + location.pathname.split("/")[1] }],
  });

  // Check if the locale is Swedish (sv_SE)
  const isSE = info.locale === "sv_SE";

  return (
    <div className={styles.block}>
      <Helmet
        title={meta.title}
        meta={meta.data}
        link={meta.link}
      />
      <Wrapper>
        <Hero />
        <section className={cn(styles.center, styles.section)}>
          <h2 className={styles.heading}>{t("COMPANYVIEW.FIELD_1.TITLE")}</h2>
          <p className={styles.text}>{t("COMPANYVIEW.FIELD_1.TEXT_CONTENT")}</p>
          <ButtonLink
            variant="primary"
            className={cn(styles.cta, styles.noMargin)}
            to={t("COMPANYVIEW.FIELD_1.CTA_LINK")}
            target="_blank"
          >
            {t("COMPANYVIEW.FIELD_1.CTA")}
          </ButtonLink>
        </section>
        <PopularBrands className={cn(styles.section, styles.center)} />
        <section className={cn(styles.section)}>
          <h2 className={cn(styles.heading, styles.center)}>{t("COMPANYVIEW.FIELD_2.TITLE")}</h2>
          <div className={styles.grid}>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_1")}>
              <img src="/assets/company/svg/vector_home.svg" alt="" />
            </InfoCard>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_2")}>
              <img src="/assets/company/svg/vector_group.svg" alt="" />
            </InfoCard>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_3")}>
              <img src="/assets/company/svg/vector_card.svg" alt="" />
            </InfoCard>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_4")}>
              <img src="/assets/company/svg/vector_currency.svg" alt="" />
            </InfoCard>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_5")}>
              <img src="/assets/tutorial/2.png" alt="" />
            </InfoCard>
            <InfoCard text={t("COMPANYVIEW.FIELD_2.LIST_ITEM_6")}>
              <img src="/assets/company/svg/vector_graph.svg" alt="" />
            </InfoCard>
          </div>
        </section>
        {isSE ? (
          <>
            <section className={cn(styles.section, styles.center, styles.narrow)}>
              <h2 className={styles.heading}>{t("COMPANYVIEW.FIELD_3.TITLE")}</h2>
              <p className={styles.text}>{t("COMPANYVIEW.FIELD_3.TEXT_CONTENT")}</p>
              <ul className={cn(styles.backgroundPlate, styles.list)}>
                <li>{t("COMPANYVIEW.FIELD_3.LIST_ITEM_1")}</li>
                <li>{t("COMPANYVIEW.FIELD_3.LIST_ITEM_2")}</li>
                <li>{t("COMPANYVIEW.FIELD_3.LIST_ITEM_3")}</li>
              </ul>
              <p className={cn(styles.text, styles.footerText)}>{t("COMPANYVIEW.FIELD_3.FOOTER")}</p>
            </section>
            <section className={cn(styles.section, styles.center, styles.narrow)}>
              <h2 className={styles.heading}>{t("COMPANYVIEW.FIELD_4.TITLE")}</h2>
              <p className={styles.text}>{t("COMPANYVIEW.FIELD_4.TEXT_CONTENT")}</p>
              <p className={styles.text}>{t("COMPANYVIEW.FIELD_4.LIST_HEADER")}</p>
              <div className={cn(styles.split, styles.mediaPair)}>
                <ul className={cn(styles.backgroundPlate, styles.list)}>
                  <li>{t("COMPANYVIEW.FIELD_4.LIST_ITEM_1")}</li>
                  <li>{t("COMPANYVIEW.FIELD_4.LIST_ITEM_2")}</li>
                  <li>{t("COMPANYVIEW.FIELD_4.LIST_ITEM_3")}</li>
                  <li>{t("COMPANYVIEW.FIELD_4.LIST_ITEM_4")}</li>
                </ul>
                <img
                  className={styles.mediaImage}
                  src="/assets/company/laptop-awardit.png"
                  alt=""
                />
              </div>
              <p className={cn(styles.text, styles.footerText)}>{t("COMPANYVIEW.FIELD_4.FOOTER")}</p>
            </section>
            <section className={cn(styles.section, styles.split, styles.wide, styles.selfService)}>
              <div className={styles.textSplit}>
                <h2 className={styles.heading}>{t("COMPANYVIEW.FIELD_5.TITLE")}</h2>
                <p className={styles.text}>{t("COMPANYVIEW.FIELD_5.TEXT_CONTENT")}</p>
                <p className={styles.listHeader}>{t("COMPANYVIEW.FIELD_5.LIST_HEADER")}</p>
                <ul className={styles.list}>
                  <li>{t("COMPANYVIEW.FIELD_5.LIST_ITEM_1")}</li>
                  <li>{t("COMPANYVIEW.FIELD_5.LIST_ITEM_2")}</li>
                  <li>{t("COMPANYVIEW.FIELD_5.LIST_ITEM_3")}</li>
                  <li>{t("COMPANYVIEW.FIELD_5.LIST_ITEM_4")}</li>
                </ul>
                <div className={cn(styles.ctaBox)}>
                  <ButtonLink
                    variant="primary"
                    className={styles.cta}
                    to={t("COMPANYVIEW.CTA_LINK_3")}
                    target="_blank"
                  >
                    {t("COMPANYVIEW.CTA_3")}
                  </ButtonLink>
                  <ButtonLink
                    variant="ghost"
                    to={t("COMPANYVIEW.CTA_LINK_1")}
                    className={styles.cta}
                    target="_blank"
                  >
                    {t("COMPANYVIEW.CTA_1")}
                  </ButtonLink>
                </div>
              </div>
              <div className={styles.overlapImages}>
                <img
                  className={styles.cardImage}
                  src="/assets/company/zg-company.png"
                  alt=""
                />
                <img
                  className={styles.phoneImage}
                  src="/assets/company/phone-zupergift.png"
                  alt=""
                />
              </div>
              <p className={cn(styles.footerText, styles.selfServiceNote)}>{t("COMPANYVIEW.FIELD_5.FOOTER")}</p>
            </section>
            <PopularBrands
              className={cn(styles.section, styles.center, styles.wide)}
              variant="trusted"
            >
              <div className={cn(styles.ctaBox, styles.buttonRow, styles.noButtonMargin)}>
                <ButtonLink
                  variant="ghost"
                  to={t("COMPANYVIEW.CTA_LINK_1")}
                  className={styles.cta}
                  target="_blank"
                >
                  {t("COMPANYVIEW.CTA_1")}
                </ButtonLink>
                <ButtonLink
                  variant="primary"
                  className={styles.cta}
                  to={t("COMPANYVIEW.CTA_LINK_2")}
                  target="_blank"
                >
                  {t("COMPANYVIEW.CTA_2")}
                </ButtonLink>
              </div>
            </PopularBrands>
          </>
        ) : null}
      </Wrapper>
    </div>
  );
};

export default CompanyView;
